import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/testIds";
import { AuthShell } from "./LoginPage";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", organization_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const result = await register(form);
    setLoading(false);
    if (result.ok) {
      if (result.email_verification_sent) {
        toast.success(`Compte créé — un e-mail de vérification a été envoyé à ${form.email}.`);
      } else {
        toast.warning("Compte créé. L'envoi du mail de vérification a échoué — vous pourrez le renvoyer depuis votre espace.");
      }
      navigate("/app/assistant", { replace: true });
    } else {
      setError(result.error || "Inscription impossible");
    }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl">Créez votre organisation.</h1>
      <p className="mt-2 text-muted-foreground">Essai gratuit 14 jours. Sans carte bancaire.</p>
      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first">Prénom</Label>
            <Input id="first" data-testid={TID.registerFirstName} value={form.first_name} onChange={onChange("first_name")} required className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="last">Nom</Label>
            <Input id="last" data-testid={TID.registerLastName} value={form.last_name} onChange={onChange("last_name")} required className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label htmlFor="org">Nom de l'organisation</Label>
          <Input id="org" data-testid={TID.registerOrgName} value={form.organization_name} onChange={onChange("organization_name")} required placeholder="Ex : Holding Dupont" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="mail">Adresse e-mail professionnelle</Label>
          <Input id="mail" data-testid={TID.registerEmail} type="email" value={form.email} onChange={onChange("email")} required autoComplete="email" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="pwd">Mot de passe (8 caractères min.)</Label>
          <Input id="pwd" data-testid={TID.registerPassword} type="password" value={form.password} onChange={onChange("password")} required minLength={8} autoComplete="new-password" className="mt-1.5" />
        </div>
        {error && <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</div>}
        <Button type="submit" disabled={loading} className="btn-pill w-full" data-testid={TID.registerSubmit}>
          {loading ? "Création…" : (<>Créer mon organisation <ArrowRight className="ml-2 h-4 w-4" /></>)}
        </Button>
      </form>
      <p className="mt-6 text-xs text-muted-foreground text-center">
        En créant un compte, vous acceptez nos <a className="text-accent link-underline" href="#">CGU</a> et notre <a className="text-accent link-underline" href="#">politique de confidentialité</a>.
      </p>
      <p className="mt-4 text-sm text-muted-foreground text-center">
        Déjà un compte ? <Link to="/login" className="text-accent link-underline font-medium">Se connecter</Link>
      </p>
    </AuthShell>
  );
}
